import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './en.json';
import de from './de.json';
import { settingsStore } from '../state/settings';

type Dict = { [k: string]: string | Dict };

/** Deep merge for translation trees (feature namespaces never overwrite each other's leaves). */
export function mergeDicts(base: Dict, extra: Dict): Dict {
  const out: Dict = { ...base };
  for (const [k, v] of Object.entries(extra)) {
    const cur = out[k];
    out[k] = typeof v === 'object' && typeof cur === 'object' ? mergeDicts(cur, v) : v;
  }
  return out;
}

/** Feature modules ship their own strings in src/features/<name>/i18n/{en,de}.json. */
const featureDicts = import.meta.glob<{ default: Dict }>('../features/*/i18n/*.json', {
  eager: true,
});
function withFeatures(lang: 'en' | 'de', base: Dict): Dict {
  return Object.entries(featureDicts)
    .filter(([path]) => path.endsWith(`/${lang}.json`))
    .reduce((acc, [, mod]) => mergeDicts(acc, mod.default), base);
}

export const resources = {
  en: { translation: withFeatures('en', en as Dict) },
  de: { translation: withFeatures('de', de as Dict) },
} as const;
export const LANGUAGES = ['en', 'de'] as const;

void i18next.use(initReactI18next).init({
  resources,
  lng: settingsStore.getState().language,
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
  returnNull: false,
});

settingsStore.subscribe((s, prev) => {
  if (s.language !== prev.language) void i18next.changeLanguage(s.language);
});

if (typeof document !== 'undefined') {
  document.documentElement.lang = settingsStore.getState().language;
  i18next.on('languageChanged', (lng) => {
    document.documentElement.lang = lng;
  });
}

export default i18next;
